import { Button } from "@volca/ui/components/ui/button";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@volca/ui/components/ui/table";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { useReportRun } from "@/profitability/query";
import type { RunSummaryFigure } from "@/profitability/types";
import { formatSummaryValue, reportError } from "@/profitability/utils";

interface FigureRow {
  key: string;
  label: string;
  published: RunSummaryFigure | undefined;
  draft: RunSummaryFigure | undefined;
}

const sameLabel = (a: RunSummaryFigure, b: RunSummaryFigure) =>
  a.label.trim().toLowerCase() === b.label.trim().toLowerCase();

/**
 * Each headline figure of either version, in the draft's order, then any
 * only the published one has. A figure is the same one by its key, or, when
 * a rebuild renamed the key, by its label.
 */
export const pairFigures = (
  published: RunSummaryFigure[],
  draft: RunSummaryFigure[],
): FigureRow[] => {
  const unpaired = [...published];
  const take = (match: (figure: RunSummaryFigure) => boolean) => {
    const index = unpaired.findIndex(match);
    return index < 0 ? undefined : unpaired.splice(index, 1)[0];
  };
  const rows: FigureRow[] = draft.map((figure) => ({
    key: figure.key,
    label: figure.label,
    draft: figure,
    published: undefined,
  }));
  for (const row of rows)
    row.published = take((figure) => figure.key === row.draft!.key);
  for (const row of rows)
    row.published ??= take((figure) => sameLabel(figure, row.draft!));
  for (const figure of unpaired)
    rows.push({
      key: `published:${figure.key}`,
      label: figure.label,
      draft: undefined,
      published: figure,
    });
  return rows;
};

const changed = ({ published, draft }: FigureRow) =>
  published?.value !== draft?.value;

const shown = (figure: RunSummaryFigure | undefined) =>
  figure ? formatSummaryValue(figure) : "—";

/**
 * Two runs' headline figures side by side: by default the published version
 * and the draft, both run on the files the template was built from (what
 * publishing would change); or a period's result and a newer run of it.
 */
export const VersionFigures = ({
  businessId,
  publishedRunId,
  draftRunId,
  before = { label: "Published", name: "the published version" },
  after = { label: "Draft", name: "the draft" },
}: {
  businessId: string;
  publishedRunId: string;
  draftRunId: string;
  /** What the first run is, as a column and in a sentence. */
  before?: { label: string; name: string };
  /** What the second run is, the one that would replace it. */
  after?: { label: string; name: string };
}) => {
  const published = useReportRun(businessId, publishedRunId);
  const draft = useReportRun(businessId, draftRunId);
  const failed = [published, draft].find(
    (query) => query.isError && !query.data,
  );
  if (failed)
    return (
      <Stack gap={2} align="start">
        <Text tone="destructive" role="alert">
          The figures could not load. {reportError(failed.error)}
        </Text>
        <Button variant="secondary" onClick={() => void failed.refetch()}>
          Try again
        </Button>
      </Stack>
    );
  if (!published.data || !draft.data)
    return (
      <Skeleton className="h-40 w-full" aria-label="Loading the figures" />
    );
  const rows = pairFigures(published.data.summary, draft.data.summary);
  const differences = rows.filter(changed).length;
  return (
    <Stack gap={3} align="stretch" data-testid="version-figures">
      <Text tone="muted">
        {differences === 0
          ? `${after.name.charAt(0).toUpperCase()}${after.name.slice(1)} gives the same headline figures as ${before.name}.`
          : `${differences} of ${rows.length} headline figures differ from ${before.name}.`}
      </Text>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Figure</TableHead>
            <TableHead className="text-right">{before.label}</TableHead>
            <TableHead className="text-right">{after.label}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.key}>
              <TableCell>{row.label}</TableCell>
              <TableCell className="text-right tabular-nums">
                {shown(row.published)}
              </TableCell>
              <TableCell
                className={cn("text-right tabular-nums", {
                  "font-semibold": changed(row),
                })}
              >
                {shown(row.draft)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Text tone="muted" variant="caption">
        Compared on the first four headline figures. A dash means the figure is
        not among them.
      </Text>
    </Stack>
  );
};
